import { safeTrim } from "../../helpers/DataHelpers";
import Dataset from "../../MachineLearning/Dataset";
import { PCAEncoder } from "../../MachineLearning/PCAReduction";
import { customTopicType } from "../PersonaGroupings/CustomTopicTable";
import { NEGATION } from "./negation";
import { NEG_WORDS } from "./negWords";
import { stemmer } from "./porterStemmer";
import { POS_WORDS } from "./posWords";
import { STOP_WORDS } from "./stopWords";

const CLOUD_WORDS = 25;
const SENT_CLOUD_WORDS = 20;
const MAX_SENTENCE_WORDS = 10;
const MIN_SENTENCE_WORDS = 3;
const MAX_DOC_WORDS = 100;
const CLUSTER_DISPLAY = 1000;
const BIGRAM_SCALE = 5;
const SENT_WINDOW = 100;

export const MAX_SCROLL_DOCS = 50;

export type customTopicsDataType = {
	customTopics: customTopicType[];
	plotPoints: number[][];
	sentences: string[];
	groups: number[];
	nGroups: number;
	noTopicIndex: number;
	multiTopicIndex: number;
};

export type topicCloudDataType = {
	cloudData: cloudDataType[];
	topicDocs: Object;
	topicDocsCounts: Object;
	topicSentiment: Object;
};

export type datasetDataType = { sentiment: number[]; groups: string[] };

let stopWords = new Set(Array.from(STOP_WORDS).filter((w) => !NEGATION.has(w)));

export type cloudDataType = { value: string; count: number };

export function textProcessor(
	docsIn: string[],
	customTopics?: customTopicType[]
) {
	let memo = {};
	let docsNGrams: Object[] = [];
	for (let doc of docsIn) {
		docsNGrams.push(getNGrams(doc, memo));
	}

	// Find most frequent terms
	let cloudData = findTopTerms(docsNGrams, CLOUD_WORDS);

	let { topicDocs, topicDocsCounts } = getDocsForWordCloutTopics(
		docsNGrams,
		cloudData,
		docsIn
	);

	// Document Sentiment
	let sentiment = findDocumentSentiment(docsIn);

	// Top positive and negative sent topics
	function findPosNegCloudData(pos: boolean) {
		const CUTOFF = 10;
		let subNGrams = docsNGrams.filter((v, i) =>
			pos ? sentiment[i] >= CUTOFF : sentiment[i] <= -CUTOFF
		);
		let subDocs = docsIn.filter((v, i) =>
			pos ? sentiment[i] >= CUTOFF : sentiment[i] <= -CUTOFF
		);
		let cloudSub = findTopTerms(subNGrams, SENT_CLOUD_WORDS * 10, true);

		for (let i = 0; i < cloudSub.length; i++) {
			cloudSub[i].count = 0;
		}

		let indexes = sampleIndex(subDocs.length, 10000);
		let cleanedDocs = indexes.map((index) =>
			removePunc(safeTrim(subDocs[index]).toLowerCase())
		);

		for (let cleanedDoc of cleanedDocs) {
			for (let i = 0; i < cloudSub.length; i++) {
				const lenBefore = cleanedDoc.length;
				const docCheck = cleanedDoc.replaceAll(cloudSub[i].value, "");
				if (lenBefore !== docCheck.length) {
					const sent = calcSentiment(docCheck.split(" "));
					const termSent = calcSentiment(
						cloudSub[i].value.split(" ")
					);
					if (pos && sent > CUTOFF && termSent > 0) {
						cloudSub[i].count += Math.abs(termSent);
					} else if (!pos && sent < -CUTOFF && termSent < 0) {
						cloudSub[i].count += Math.abs(termSent);
					}
				}
			}
		}

		cloudSub = cloudSub
			.sort((a, b) => b.count - a.count)
			.slice(0, SENT_CLOUD_WORDS);
		let docsSub = docsIn.filter((v, i) =>
			pos ? sentiment[i] >= CUTOFF : sentiment[i] <= -CUTOFF
		);
		let { topicDocs, topicDocsCounts } = getDocsForWordCloutTopics(
			subNGrams,
			cloudSub,
			docsSub
		);
		let topics = cloudSub.map((v) => v.value);
		let topicSentiment = findTermSentiment(subNGrams, topics);
		const topicCloudData: topicCloudDataType = {
			cloudData: cloudSub,
			topicDocs: topicDocs,
			topicDocsCounts: topicDocsCounts,
			topicSentiment: topicSentiment,
		};
		return topicCloudData;
	}
	let posTopicCloudData = findPosNegCloudData(true);
	let negTopicCloudData = findPosNegCloudData(false);

	// Sample sentences
	let sentences = sampleSentences(docsIn);
	let sentenceNGrams: Object[] = [];
	for (let sent of sentences) {
		sentenceNGrams.push(getNGrams(sent, memo));
	}

	let plotPoints = getPlotPoints(sentenceNGrams, cloudData);

	let groups: string[] = undefined;
	if (customTopics !== undefined && hasTopics(customTopics)) {
		const classes = classifyByTopic(docsNGrams, customTopics);
		let { flattened } = flattenClasses(classes);
		let names = customTopics.map((v) => v.topicName);
		names.push(...["Multiple Topics", "Other"]);
		groups = flattened.map((n) => names[n]);
	}
	const topicCloudData: topicCloudDataType = {
		cloudData: cloudData,
		topicDocs: topicDocs,
		topicDocsCounts: topicDocsCounts,
		topicSentiment: undefined,
	};
	const customTopicsData: customTopicsDataType = {
		customTopics: undefined,
		plotPoints: plotPoints,
		sentences: sentences,
		groups: undefined,
		nGroups: undefined,
		noTopicIndex: undefined,
		multiTopicIndex: undefined,
	};
	const datasetData: datasetDataType = {
		sentiment: sentiment,
		groups: groups,
	};

	return {
		posTopicCloudData,
		negTopicCloudData,
		topicCloudData,
		customTopicsData,
		datasetData,
	};
}

export function sampleSentences(docs: string[]) {
	// Sample sentences for display
	let indexes = sampleIndex(docs.length, CLUSTER_DISPLAY * 4);
	let sampledStrict: string[] = [];
	let sampledLoose: string[] = [];
	let memo = {};
	for (let index of indexes) {
		let sentences = extractSentences(docs[index]);
		let ndx = sampleIndex(sentences.length, 1)[0];
		let sentence = capitalizeFirstLetter(safeTrim(sentences[ndx]));

		let words = splitWords(sentence).slice(0, MAX_SENTENCE_WORDS);
		sentence = words.join(" ");
		let cleanedWords: string[] = [];
		for (let word of words) {
			let stemmed = normalize(word, memo);
			if (stemmed.length <= 1) {
				continue;
			}
			cleanedWords.push(stemmed);
		}
		cleanedWords = cleanedWords.filter((w) => !stopWords.has(w));

		if (sampledLoose.length < CLUSTER_DISPLAY && cleanedWords.length > 1) {
			sampledLoose.push(sentence);
		}

		if (cleanedWords.length >= MIN_SENTENCE_WORDS) {
			sampledStrict.push(sentence);
		}

		if (sampledStrict.length >= CLUSTER_DISPLAY) {
			break;
		}
	}

	let foundEnough = sampledStrict.length >= CLUSTER_DISPLAY;
	let sentences = foundEnough ? sampledStrict : sampledLoose;
	return sentences;
}

export function calcCustomTopicsData(
	sentences: string[],
	topics: customTopicType[],
	cloudData: cloudDataType[],
	prevPlotPoints: number[][],
	docs: string[]
) {
	let memo = {};
	let sentenceNGrams: Object[] = [];
	for (let sent of sentences) {
		sentenceNGrams.push(getNGrams(sent, memo));
	}
	let docNGrams = [];
	for (let doc of docs) {
		docNGrams.push(getNGrams(doc || "", memo));
	}
	let classes = classifyByTopic(sentenceNGrams, topics);
	let { flattened, multiTopicIndex, noTopicIndex, nGroups } =
		flattenClasses(classes);

	if (topics.length === 0) {
		classes = undefined;
	}

	let plotPoints = getPlotPoints(
		sentenceNGrams,
		cloudData,
		classes,
		prevPlotPoints
	);
	for (let topic of topics) {
		const terms = extractTopicTerms(topic, memo);

		let docCount = 0;
		let termDocCount = {};
		for (let term of terms) {
			for (let doc of docNGrams) {
				// doc = doc || "";
				// doc = normalizeString(doc, memo);
				const hasTerm = doc[term] !== undefined;
				if (hasTerm) {
					docCount += 1;
					termDocCount[term] = 1 + (termDocCount[term] || 0);
				}
			}
		}
		if (terms.length === 0) {
			topic.nDocs = undefined;
		} else {
			topic.nDocs = docCount;
		}
		let termsSent = 0;

		topic.sentiment = termsSent;
	}
	const customTopicsData: customTopicsDataType = {
		customTopics: topics,
		plotPoints: plotPoints,
		sentences: sentences,
		groups: flattened,
		nGroups: nGroups,
		noTopicIndex: noTopicIndex,
		multiTopicIndex: multiTopicIndex,
	};

	return customTopicsData;
}

function getPlotPoints(
	sentenceNGrams: Object[],
	cloudData: cloudDataType[],
	classes?: number[][],
	prevPlotPoints?: number[][]
) {
	let plotPoints: number[][] = [];
	let topTerms = cloudData.map((d) => d.value);
	let vectors = createVectors(sentenceNGrams, topTerms, classes);
	let pca = new PCAEncoder(2);
	pca.train(vectors);
	plotPoints = pca.applyProjection(vectors);
	plotPoints = roundPlotPoints(plotPoints);
	if (prevPlotPoints !== undefined) {
		plotPoints = reversIfNeeded(plotPoints, prevPlotPoints);
	}

	return plotPoints;
}

function reversIfNeeded(plotPoints: number[][], prevPlotPoints: number[][]) {
	let indexes = sampleIndex(plotPoints.length, 1000);
	for (let i = 0; i < 2; i++) {
		let mse1 = 0;
		let mse2 = 0;
		for (let index of indexes) {
			mse1 += Math.abs(plotPoints[index][i] - prevPlotPoints[index][i]);
			mse2 += Math.abs(-plotPoints[index][i] - prevPlotPoints[index][i]);
		}
		if (mse2 < mse1) {
			for (let k = 0; k < plotPoints.length; k++) {
				plotPoints[k][i] = -plotPoints[k][i];
			}
		}
	}

	return plotPoints;
}

function hasTopics(customTopics: customTopicType[]) {
	for (let topic of customTopics) {
		if (topic.selected && topic.phrases !== "") {
			return true;
		}
	}
	return false;
}

function roundPlotPoints(plotPoints: number[][]) {
	let maxX = -Infinity;
	let minX = Infinity;
	let maxY = -Infinity;
	let minY = Infinity;

	for (let i = 0; i < plotPoints.length; i++) {
		maxX = Math.max(plotPoints[i][0], maxX);
		minX = Math.min(plotPoints[i][0], minX);
		maxY = Math.max(plotPoints[i][1], maxY);
		minY = Math.min(plotPoints[i][1], minY);
	}
	if (Math.abs(maxX - minX) < 0.001 || Math.abs(maxY - minY) < 0.001) {
		return plotPoints;
	}
	for (let i = 0; i < plotPoints.length; i++) {
		plotPoints[i][0] = (plotPoints[i][0] - minX) / (maxX - minX);
		plotPoints[i][1] = (plotPoints[i][1] - minY) / (maxY - minY);
	}
	for (let i = 0; i < plotPoints.length; i++) {
		plotPoints[i][0] = Math.round(plotPoints[i][0] * 1000) / 1000;
		plotPoints[i][1] = Math.round(plotPoints[i][1] * 1000) / 1000;
	}
	return plotPoints;
}

function extractTopicTerms(topic: customTopicType, memo: Object) {
	return topic.phrases
		.split(",")
		.map((v) => normalizeString(v, memo))
		.filter((k) => k.length > 0);
}

function classifyByTopic(docsNGrams: Object[], topicsIn: customTopicType[]) {
	let topicTerms = [];
	let memo = {};
	for (let topic of topicsIn) {
		topicTerms.push(extractTopicTerms(topic, memo));
	}

	let docClasses: number[][] = Array(docsNGrams.length)
		.fill(0)
		.map((v) => Array(topicTerms.length).fill(0));
	for (let i = 0; i < docsNGrams.length; i++) {
		for (let j = 0; j < topicTerms.length; j++) {
			for (let term of topicTerms[j]) {
				if (docsNGrams[i][term] !== undefined && topicsIn[j].selected) {
					docClasses[i][j] = 1;
					break;
				}
			}
		}
	}
	return docClasses;
}

function flattenClasses(classes: number[][]) {
	let flattened: number[] = [];
	const multiTopicIndex = classes[0].length;
	const noTopicIndex = multiTopicIndex + 1;
	const nGroups = noTopicIndex + 1;
	for (let current of classes) {
		let theClass = noTopicIndex;
		for (let i = 0; i < current.length; i++) {
			if (current[i] === 1 && theClass !== noTopicIndex) {
				theClass = multiTopicIndex;
				break;
			} else if (current[i] !== 0) {
				theClass = i;
			}
		}
		flattened.push(theClass);
	}
	return { flattened, multiTopicIndex, noTopicIndex, nGroups };
}

function getNGrams(doc: string, memo: Object) {
	if (doc === null || doc === undefined) {
		doc = "";
	}
	let words = getCleanedWords(doc);
	let docNGrams: Object = {};
	for (let k = 1; k <= 3; k++) {
		splitNGrams(words, k, docNGrams, memo);
	}

	return docNGrams;
}

function getCleanedWords(doc: string) {
	if (doc === undefined) {
		return [""];
	}
	let words = splitWords(doc);
	words = words.slice(0, MAX_DOC_WORDS);
	words = words.map((w) => lower(w));
	words = words.map((w) => removePunc(w));
	words = words.filter((w) => w.length > 0);
	return words;
}

function normalizeString(str: string, memo: Object, removeStop = true) {
	return safeTrim(
		str
			.split(" ")
			.map((w) => normalizeWord(w, memo, removeStop))
			.filter((w) => w.length > 0)
			.join(" ")
	);
}

function normalizeWord(word: string, memo: Object, removeStop = true) {
	word = safeTrim(word);
	if (removeStop && stopWords.has(word)) {
		return "";
	}

	word = normalize(word, memo);
	return word;
}

function findTermSentiment(docsNGrams: Object[], terms: string[]) {
	let topicSentiment = {};
	let topicCounts = {};
	let memo = {};
	let indexes = sampleIndex(docsNGrams.length, 10000);
	for (let index of indexes) {
		const doc = docsNGrams[index];
		let val = "";
		for (let term of Object.keys(doc)) {
			if (term.includes(" ")) {
				break;
			}
			if (term.length > 0) {
				val += term + " ";
			}
		}
		val = safeTrim(val);

		for (let term of terms) {
			let valNormalized = normalizeString(term, memo);
			let ndx = val.indexOf(valNormalized);
			if (ndx > 0) {
				let before = safeTrim(val.slice(0, ndx)).split(" ");
				before = before.slice(
					Math.max(0, before.length - SENT_WINDOW),
					before.length
				);
				let after = safeTrim(val.slice(ndx))
					.split(" ")
					.slice(0, SENT_WINDOW);

				let sent = calcSentiment([...before, ...after]);
				sent = sent > 0 ? 1 : Math.abs(sent) <= 0.1 ? 0 : -1;
				topicCounts[term] = 1 + (topicCounts[term] || 0);
				topicSentiment[term] = sent + (topicSentiment[term] || 0);
			}
		}
	}
	for (let key of Object.keys(topicSentiment)) {
		topicSentiment[key] /= topicCounts[key];
		topicSentiment[key] = Math.round(topicSentiment[key] * 100);
	}

	return topicSentiment;
}

function getDocsForWordCloutTopics(
	docsNGrams: Object[],
	cloudData: cloudDataType[],
	docs: string[]
) {
	let topicDocs = {};
	let topicDocsCounts = {};
	let memo = {};
	for (let topic of cloudData) {
		let topicCleaned = normalizeString(topic.value, memo);
		topicDocs[topic.value] = [];
		for (let i = 0; i < docsNGrams.length; i++) {
			if (docsNGrams[i][topicCleaned] !== undefined) {
				if (topicDocs[topic.value].length < MAX_SCROLL_DOCS) {
					topicDocs[topic.value].push(docs[i]);
				}
				topicDocsCounts[topic.value] =
					1 + (topicDocsCounts[topic.value] || 0);
			}
		}
	}

	return { topicDocs, topicDocsCounts };
}

function findDocumentSentiment(docs: string[]) {
	let sentiment: number[] = [];
	for (let doc of docs) {
		const words = safeTrim(removePunc(doc)).toLowerCase().split(" ");
		sentiment.push(words.length >= 1 ? calcSentiment(words) : 0);
	}
	return sentiment;
}

function createVectors(
	docsNGrams: Object[],
	topTerms: string[],
	classes?: number[][]
) {
	let vectors: number[][] = [];
	let topTermsCleaned: string[] = [];
	let termsIndex = {};
	let index = 0;

	let memo = {};
	for (let term of topTerms) {
		let termCleaned = normalizeString(term, memo);
		topTermsCleaned.push(termCleaned);
		termsIndex[termCleaned] = index;
		index += 1;
	}

	for (let doc of docsNGrams) {
		let vector = Array(topTerms.length).fill(0);
		for (let term of topTermsCleaned) {
			if (doc[term] !== undefined) {
				vector[termsIndex[term]] = 1;
			}
		}
		vectors.push(vector);
	}

	if (classes !== undefined) {
		let classCounts = {};
		for (let i = 0; i < classes.length; i++) {
			for (let j = 0; j < classes[i].length; j++) {
				if (classes[i][j] === 1) {
					classCounts[j] = 1 + (classCounts[j] || 0);
				}
			}
		}
		let scale = Array(classes[0].length)
			.fill(0)
			.map((v, i) => 20 * (1 - classCounts[i] / classes.length));

		for (let i = 0; i < vectors.length; i++) {
			let toAppend = classes[i].map(
				(v, index) => (classes[0].length / 20) * v
			);
			vectors[i].push(...toAppend);
		}
	}

	return vectors;
}

function findTopTerms(
	docsNGrams: Object[],
	nTerms: number,
	excludeSentWords = false
) {
	let indexes = sampleIndex(docsNGrams.length, 10000);
	let counts = {};

	for (let index of indexes) {
		for (let term of Object.keys(docsNGrams[index])) {
			const isBigram = term.includes(" ");
			counts[term] =
				1 * (isBigram ? BIGRAM_SCALE : 1) + (counts[term] || 0);
		}
	}

	let stemMap = {};
	for (let index of indexes) {
		for (let term of Object.keys(docsNGrams[index])) {
			let currentTerm = stemMap[term] || docsNGrams[index][term];
			let candidateTerm = docsNGrams[index][term];
			stemMap[term] =
				currentTerm.length < candidateTerm.length
					? currentTerm
					: candidateTerm;
		}
	}
	let topNGrams = Object.keys(counts)
		.filter((w) => !STOP_WORDS.has(w))
		.sort((a, b) => counts[b] - counts[a]);
	if (excludeSentWords) {
		topNGrams = topNGrams.filter(
			(w) => !POS_WORDS.has(w) && !NEG_WORDS.has(w)
		);
	}
	topNGrams = topNGrams.slice(0, nTerms);
	let out: cloudDataType[] = [];
	for (let nGram of topNGrams) {
		out.push({ value: stemMap[nGram], count: counts[nGram] });
	}
	return out;
}

function capitalizeFirstLetter(string: string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

function removePunc(txt: string) {
	const toRemove = ["!", ",", "?", "/", '"', "'", "-", "(", ")", "."];
	if (txt === undefined) {
		return "";
	}
	for (let val of toRemove) {
		txt = txt.replaceAll(val, "");
	}
	return txt.replaceAll("  ", " ");
}

function lower(txt: string) {
	return txt.toLowerCase();
}

function removeNumbers(txt: string) {
	return txt.replace(/[0-9]/g, "");
}

function calcSentiment(words: string[]) {
	let positive = 0;
	let negative = 0;
	const nWords = words.length;

	words = words.filter(
		(w) =>
			!STOP_WORDS.has(w) ||
			NEGATION.has(w) ||
			POS_WORDS.has(w) ||
			NEG_WORDS.has(w)
	);
	for (let i = 0; i < words.length; i++) {
		const word = words[i];
		let prevWord = i > 0 ? words[i - 1] : "";
		let nextWord = i + 1 < words.length ? words[i + 1] : "";

		const isPositive =
			(POS_WORDS.has(word) && !NEGATION.has(prevWord)) ||
			(NEG_WORDS.has(word) && NEGATION.has(prevWord));
		const isNegative =
			(NEG_WORDS.has(word) && !NEGATION.has(prevWord)) ||
			(POS_WORDS.has(word) && NEGATION.has(prevWord));

		if (isPositive) {
			positive += 1;
		}
		if (isNegative) {
			if (nextWord === "free") {
				positive += 1;
			} else {
				negative += 1;
			}
		}
	}

	let sentiment = 0;
	if (words.length > 0) {
		sentiment = Math.round((100 * (positive - negative)) / nWords);
	}
	return sentiment;
}

function normalize(w: string, memo: Object) {
	if (memo[w] !== undefined) {
		return memo[w];
	}

	let nw = w.toLowerCase().replace(/[^a-z\'A-Z0-9\u00C0-\u00ff ]+/g, "");
	nw = nw.replace("http", "");
	nw = removeNumbers(nw);
	nw = removePunc(nw);
	try {
		nw = stemmer(nw);
	} catch {}
	memo[w] = nw;
	return nw;
}

function extractSentences(doc: string) {
	return doc.match(/[^\.!\?]+[\.!\?]+/g) || [doc];
}

function splitWords(doc: string) {
	return doc.split(/[\s,\"]+/);
}

function splitNGrams(words: string[], N: number, nGrams: Object, memo: Object) {
	let nGramsAr = words.map((w, i) => i).map((i) => words.slice(i, i + N));
	for (let nGram of nGramsAr) {
		let nGramCleaned = nGram
			.map((n) => normalizeWord(n, memo))
			.filter((w) => w.length > 0);
		if (nGramCleaned.length === 0) {
			continue;
		}

		let fullNGram = safeTrim(nGram.join(" "));
		let cleanedNGram = safeTrim(nGramCleaned.join(" "));
		let currentFull = nGrams[cleanedNGram] || fullNGram;

		nGrams[cleanedNGram] =
			fullNGram.length < currentFull.length ? fullNGram : currentFull;
	}
}

function sampleIndex(top: number, n: number) {
	return Dataset.shuffle(
		Array(top)
			.fill(0)
			.map((v, i) => i)
	).slice(0, n) as number[];
}
